import React, { useState } from 'react';
import { find, pathOr, pipe, prop, propEq, sortBy, map, findIndex, path } from 'ramda';

import { filterOffNils, getQueryStringValue } from '../../utils';
import { renderWhenTrue, renderWhenTrueOtherwise } from '../../utils/rendering';
import { BLOCK_ELEMENTS, ELEMENTS_TYPES } from '../../utils/constants';
import { Chart } from '../chart';
import { Container, Header, Title, Tab, TabsList, TabIndicator, Carousel } from './chartCarousel.styles';
import { Button, Navigation, NextArrowIcon, PreviousArrowIcon } from '../cardCarousel/cardCarousel.styles';
import { isPuppeteer } from '../../utils/media';

const addTitle = (elements) => {
  return elements.map((element) => {
    if (element.name !== BLOCK_ELEMENTS.CHART) {
      return element;
    }

    const { description } = element.value.meta;
    const matchedDesc = description.match(/\[\[.+\]\]/g);

    if (matchedDesc) {
      element.value.meta.customTitle = description.replace(/(\[\[)|(\]\])/g, '');
    }

    return element;
  });
};

const renderTab = (tabsIndicators) => (elements, key) => (
  <Tab key={key}>
    <Chart elements={addTitle(elements)} tabs={tabsIndicators} />
  </Tab>
);

const renderNone = () => null;

const handleTabClick = (setCurrentSlide, index) => () => {
  setCurrentSlide(index);
};

const renderTabsIndicators = (descriptions, currentSlide, setCurrentSlide) => {
  const tabs = descriptions.map((name) => {
    const items = name.match(/\[\[.+\]\]/g);

    return pathOr('', [0], items).replace(/(\[\[)|(\]\])/g, '');
  });

  return (
    <TabsList>
      {tabs.map((name, index) => (
        <TabIndicator key={index} selected={currentSlide === index} onClick={handleTabClick(setCurrentSlide, index)}>
          {name}
        </TabIndicator>
      ))}
    </TabsList>
  );
};

const renderHeader = ({ currentSlide, tabs, handleNextClick, handlePreviousClick }) =>
  renderWhenTrue(() => (
    <Header short={true}>
      <Title>
        {currentSlide + 1} of {tabs.length}
      </Title>
      <Navigation>
        <Button onClick={handlePreviousClick}>
          <PreviousArrowIcon />
        </Button>
        <Button onClick={handleNextClick}>
          <NextArrowIcon />
        </Button>
      </Navigation>
    </Header>
  ))(!!tabs && !isPuppeteer);

export const ChartCarousel = ({ elements = [] } = {}) => {
  const tabs = pipe(
    find(propEq('name', BLOCK_ELEMENTS.CHARTS_AND_CARD)),
    ({ value = [] } = {}) => value,
    sortBy(prop('order')),
    map(({ elements: data = [] }) => {
      const chart = find(propEq('type', ELEMENTS_TYPES.STATE))(data);

      const items = [
        find(propEq('type', ELEMENTS_TYPES.MARKDOWN))(data),
        find(propEq('type', ELEMENTS_TYPES.CONNECTION))(data),
      ].filter(filterOffNils);

      return [
        { name: BLOCK_ELEMENTS.CARD, items },
        { ...chart, name: BLOCK_ELEMENTS.CHART },
      ];
    })
  )(elements);

  const chartId = getQueryStringValue('chart');
  const chartIndexInTabs = chartId && findIndex((item) => `${path([1, 'id'], item)}` === chartId)(tabs);

  const [currentSlide, setCurrentSlide] = useState(() => {
    if (chartIndexInTabs > 0) {
      return chartIndexInTabs;
    }

    return 0;
  });

  const descriptions = tabs.map((elements) => {
    const chart = find(propEq('name', BLOCK_ELEMENTS.CHART))(elements) || {};
    return pathOr('', ['value', 'meta', 'description'], chart);
  });

  const max = tabs.length - 1;

  const handleNextClick = () => {
    if (currentSlide < max) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePreviousClick = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const tabsIndicators = renderTabsIndicators(descriptions, currentSlide, setCurrentSlide);

  const renderListOrSlider = (list) =>
    renderWhenTrueOtherwise(
      () => list,
      () => (
        <Carousel
          showThumbs={false}
          renderArrowPrev={renderNone}
          renderArrowNext={renderNone}
          renderIndicator={renderNone}
          selectedItem={currentSlide}
        >
          {list}
        </Carousel>
      )
    )(isPuppeteer);

  return (
    <Container>
      {renderHeader({
        currentSlide,
        tabs,
        handleNextClick,
        handlePreviousClick,
      })}
      {renderListOrSlider(tabs.map(renderTab(tabsIndicators)))}
    </Container>
  );
};
